import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { feedbackApi } from '../../api/feedback';

let nextKey = 0;

/**
 * Files attached to a reply. Each one uploads as soon as it's picked:
 * { key, name, size, status: 'uploading' | 'ready' | 'error', url, error }.
 * Removing a file only drops it from the reply (there's no delete API).
 */
export default function useReplyAttachments() {
  const [items, setItems] = useState([]);
  const controllers = useRef(new Map());
  const files = useRef(new Map());

  const patch = (key, changes) => setItems((list) => list.map((item) => (item.key === key ? { ...item, ...changes } : item)));

  const upload = useCallback(async (key) => {
    const file = files.current.get(key);
    if (!file) return;
    const controller = new AbortController();
    controllers.current.set(key, controller);
    patch(key, { status: 'uploading', error: null });
    try {
      const { url } = await feedbackApi.uploadFile(file, { signal: controller.signal });
      patch(key, { status: 'ready', url });
    } catch (err) {
      if (controller.signal.aborted) return;
      patch(key, { status: 'error', error: err instanceof ApiError ? err.message : 'Upload failed.' });
    } finally {
      controllers.current.delete(key);
    }
  }, []);

  const add = useCallback(
    (picked) => {
      const added = picked.map((file) => {
        nextKey += 1;
        const key = `file-${nextKey}`;
        files.current.set(key, file);
        return { key, name: file.name, size: file.size, status: 'uploading', url: null, error: null };
      });
      setItems((list) => [...list, ...added]);
      added.forEach((item) => upload(item.key));
    },
    [upload],
  );

  const remove = useCallback((key) => {
    controllers.current.get(key)?.abort();
    controllers.current.delete(key);
    files.current.delete(key);
    setItems((list) => list.filter((item) => item.key !== key));
  }, []);

  /** Replace everything, e.g. with a failed reply's already-uploaded files ("Send again"). */
  const reset = useCallback((ready = []) => {
    controllers.current.forEach((c) => c.abort());
    controllers.current.clear();
    files.current.clear();
    setItems(
      ready.map((a) => {
        nextKey += 1;
        return { key: `file-${nextKey}`, name: a.fileName, size: a.sizeBytes, status: 'ready', url: a.rawUrl, error: null };
      }),
    );
  }, []);

  useEffect(() => {
    const map = controllers.current;
    return () => map.forEach((c) => c.abort());
  }, []);

  return {
    items,
    add,
    remove,
    retry: upload,
    reset,
    uploading: items.some((i) => i.status === 'uploading'),
    readyUrls: items.filter((i) => i.status === 'ready').map((i) => i.url),
  };
}
